/**
 * 错误处理工具函数
 * 专门用于处理浏览器扩展相关的错误和其他常见错误
 */

// 浏览器扩展相关错误的关键词
const EXTENSION_ERROR_KEYWORDS = [
  'message port closed',
  'Extension context invalidated',
  'runtime.lastError',
  'chrome-extension://',
  'moz-extension://',
  'Extension host',
  'Context invalidated',
  'message channel closed'
];

/**
 * 检查是否为浏览器扩展相关错误
 */
export const isExtensionRelatedError = (error: any): boolean => {
  if (!error) return false;
  
  const errorMessage = typeof error === 'string' ? error : 
                      error.message || 
                      error.toString() || 
                      '';
  
  return EXTENSION_ERROR_KEYWORDS.some(keyword => 
    errorMessage.toLowerCase().includes(keyword.toLowerCase())
  );
};

/**
 * 安全地执行AbortController.abort()
 */
export const safeAbort = (controller: AbortController, context: string = ''): void => {
  try {
    controller.abort();
  } catch (error) {
    if (!isExtensionRelatedError(error)) {
      console.warn(`${context} AbortController.abort() 错误:`, error);
    }
  }
};

/**
 * 安全地清除定时器
 */
export const safeClearTimeout = (timeoutId: NodeJS.Timeout | number, context: string = ''): void => {
  try {
    clearTimeout(timeoutId);
  } catch (error) {
    if (!isExtensionRelatedError(error)) {
      console.warn(`${context} clearTimeout 错误:`, error);
    }
  }
};

/**
 * 处理网络请求错误
 */
export const handleFetchError = (error: any, context: string = ''): {
  isExtensionError: boolean;
  message: string;
  details: string;
} => {
  if (isExtensionRelatedError(error)) {
    return {
      isExtensionError: true,
      message: '请求被浏览器扩展干扰',
      details: '某些浏览器扩展可能会干扰网络请求。建议：\n1. 尝试在隐私模式下访问\n2. 禁用所有扩展后重试\n3. 使用其他浏览器测试'
    };
  }
  
  const errorMessage = error instanceof Error ? error.message : String(error);
  
  if (errorMessage.includes('abort')) {
    return {
      isExtensionError: false,
      message: '请求超时',
      details: `${context ? context + ': ' : ''}请求在预定时间内未收到响应。请检查网络连接和服务器状态。`
    };
  }
  
  return {
    isExtensionError: false,
    message: `请求失败: ${errorMessage}`,
    details: error instanceof Error && error.stack ? error.stack : '无错误堆栈信息'
  };
};

/**
 * 创建安全的fetch请求包装器
 */
export const safeFetch = async (
  url: string, 
  options: RequestInit = {}, 
  timeout: number = 8000,
  context: string = ''
): Promise<Response> => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => {
    safeAbort(controller, context);
  }, timeout);
  
  try {
    const response = await fetch(url, {
      ...options,
      signal: controller.signal
    });
    
    safeClearTimeout(timeoutId, context);
    return response;
  } catch (error) {
    safeClearTimeout(timeoutId, context);
    
    const errorInfo = handleFetchError(error, context);
    if (errorInfo.isExtensionError) {
      console.warn(`${context} 检测到浏览器扩展相关错误，已忽略:`, error);
    } else {
      console.error(`${context} 网络请求错误:`, error);
    }
    
    throw error;
  }
};

/**
 * 设置全局错误处理
 */
export const setupGlobalErrorHandling = (): void => {
  // 处理未捕获的Promise拒绝
  window.addEventListener('unhandledrejection', (event) => {
    if (isExtensionRelatedError(event.reason)) {
      console.warn('检测到浏览器扩展相关的未处理Promise拒绝，已忽略:', event.reason);
      event.preventDefault(); // 阻止在控制台显示
      return;
    }
    
    console.error('未处理的Promise拒绝:', event.reason);
  });
  
  // 处理未捕获的JavaScript错误
  window.addEventListener('error', (event) => {
    if (isExtensionRelatedError(event.error)) {
      console.warn('检测到浏览器扩展相关的JavaScript错误，已忽略:', event.error);
      event.preventDefault(); // 阻止在控制台显示
      return;
    }
    
    console.error('未处理的JavaScript错误:', event.error);
  });
}; 